/**
 * SC-03: 案件詳細
 *
 * 選択した営業案件の詳細情報と変更履歴を表示
 * 編集画面（SalesCaseEditPage）の読み取り専用バージョン
 *
 * @created 2025-11-16
 * @updated 2025-12-11
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Edit, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { salesCasesApi, salesMasterApi } from '../../services/salesApi';
import type { SalesCase, SalesCasePhaseHistory, SalesPhaseContribution, CloseReason, ResourceAllocation, SalesPhase } from '../../services/salesApi';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { formatDateJST, formatDateTimeJST } from '../../utils/dateUtils';
import { ActivityTimeline } from '../../components/Sales/ActivityTimeline';
import { extractErrorMessage } from '../../utils/errorHandler';
import ResourceAllocationTable from '../../components/Sales/ResourceAllocationTable';

const SalesCaseDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const { user, salesPermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salesCase, setSalesCase] = useState<SalesCase | null>(null);
  const [phaseHistory, setPhaseHistory] = useState<SalesCasePhaseHistory[]>([]);
  const [phaseContributions, setPhaseContributions] = useState<SalesPhaseContribution[]>([]);
  const [totalContribution, setTotalContribution] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [closeReasons, setCloseReasons] = useState<CloseReason[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [phases, setPhases] = useState<SalesPhase[]>([]);

  // 要員計画データ
  const [resourcesData, setResourcesData] = useState<Partial<ResourceAllocation>[]>([]);
  // 指定年度（変更可能）
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const [fiscalYear, setFiscalYear] = useState(() => currentYear);

  useEffect(() => {
    if (caseId) {
      fetchCaseDetail();
      fetchPhaseHistory();
      fetchPhaseContributions();
      fetchCloseReasons();
      fetchActivities();
      fetchAllocations();
      fetchPhases();
    }
  }, [caseId]);

  const fetchPhases = async () => {
    try {
      const response = await salesMasterApi.getPhases();
      if (response.data.success) {
        setPhases(response.data.data);
      }
    } catch (err) {
      console.error('フェーズ取得エラー:', err);
    }
  };

  // 要員配置データ取得
  const fetchAllocations = async () => {
    try {
      const response = await salesCasesApi.getCaseAllocations(parseInt(caseId!));
      if (response.data.success) {
        // APIレスポンスをResourceAllocation形式に変換
        const allocations = response.data.data.map(a => ({
          resource_name: a.resource_name,
          resource_type: a.resource_type,
          employee_id: a.employee_id || '',
          vendor_id: a.vendor_id,
          unit_price: a.unit_price,
          cost_rate: a.cost_rate,
          start_month: a.start_month,
          end_month: a.end_month,
        }));
        setResourcesData(allocations);
      }
    } catch (err) {
      console.error('要員配置取得エラー:', err);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await salesCasesApi.getCaseActivities(parseInt(caseId!));
      if (response.data.success) {
        setActivities(response.data.data);
      }
    } catch (err) {
      console.error('行動履歴取得エラー:', err);
    }
  };

  const handleAddActivity = async (activity: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      await salesCasesApi.addCaseActivity(parseInt(caseId!), activity);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴追加エラー:', err);
      throw err;
    }
  };

  const handleUpdateActivity = async (activityId: number, activity: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      await salesCasesApi.updateCaseActivity(parseInt(caseId!), activityId, activity);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴更新エラー:', err);
      throw err;
    }
  };

  const handleDeleteActivity = async (activityId: number) => {
    try {
      await salesCasesApi.deleteCaseActivity(parseInt(caseId!), activityId);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴削除エラー:', err);
      throw err;
    }
  };

  const fetchCloseReasons = async () => {
    try {
      const response = await salesMasterApi.getCloseReasons();
      if (response.data.success) {
        setCloseReasons(response.data.data.all);
      }
    } catch (err) {
      console.error('クローズ理由取得エラー:', err);
    }
  };

  const fetchCaseDetail = async () => {
    try {
      setLoading(true);
      const response = await salesCasesApi.getCaseById(parseInt(caseId!));

      if (response.data.success) {
        setSalesCase(response.data.data);
      }
    } catch (err) {
      console.error('案件詳細取得エラー:', err);
      setError(extractErrorMessage(err, '案件詳細の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPhaseHistory = async () => {
    try {
      const response = await salesCasesApi.getCasePhaseHistory(parseInt(caseId!));

      if (response.data.success) {
        setPhaseHistory(response.data.data.history);
      }
    } catch (err) {
      console.error('フェーズ履歴取得エラー:', err);
    }
  };

  const fetchPhaseContributions = async () => {
    try {
      const response = await salesCasesApi.getPhaseContributions(parseInt(caseId!));

      if (response.data.success) {
        setPhaseContributions(response.data.data.phase_contributions);
        setTotalContribution(response.data.data.total_contribution);
      }
    } catch (err) {
      console.error('フェーズ別期待受注額取得エラー:', err);
      // エラーは無視（権限がない場合は表示しない）
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await salesCasesApi.deleteCase(parseInt(caseId!));
      navigate('/sales/cases');
    } catch (err) {
      console.error('案件削除エラー:', err);
      setError(extractErrorMessage(err, '案件の削除に失敗しました'));
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return formatDateJST(dateString);
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    return formatDateTimeJST(dateString, true);
  };

  const getStatusLabel = (status: string, closeResult?: string): { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'primary' } => {
    switch (status) {
      case 'active':
        return { label: '商談中', color: 'primary' };
      case 'suspended':
        return { label: '保留', color: 'warning' };
      case 'closed':
        return closeResult === 'won' 
          ? { label: '受注', color: 'success' }
          : { label: '失注', color: 'error' };
      default:
        return { label: status, color: 'default' };
    }
  };

  // 要員配置から計算した金額を表示用に算出
  const calculatedFromResources = useMemo(() => {
    let totalSales = 0;
    let totalCost = 0;

    resourcesData.forEach(r => {
      if (r.start_month && r.end_month) {
        const start = new Date(r.start_month);
        const end = new Date(r.end_month);
        // 月数を計算
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
        totalSales += (Number(r.unit_price) || 0) * months;
        totalCost += (Number(r.cost_rate) || 0) * months;
      }
    });

    return { sales: totalSales, cost: totalCost, grossProfit: totalSales - totalCost };
  }, [resourcesData]);

  // 要員配置から最も早い開始月を取得
  const earliestStartMonth = useMemo(() => {
    if (resourcesData.length === 0) return '';

    const startMonths = resourcesData
      .filter(r => r.start_month)
      .map(r => r.start_month as string);

    if (startMonths.length === 0) return '';

    // 最も早い月を取得（YYYY-MM形式でソート可能）
    return startMonths.sort()[0].substring(0, 7); // YYYY-MMに正規化
  }, [resourcesData]);

  // 指定年度の月別予実計算（要員配置から自動計算）
  const monthlyBreakdown = useMemo(() => {
    const months: Array<{
      month: number;
      plannedSales: number;
      plannedCost: number;
      plannedOutsourceRate: number;
      actualSales: number;
      actualCost: number;
      actualOutsourceRate: number;
    }> = [];

    for (let m = 1; m <= 12; m++) {
      let plannedSales = 0;
      let plannedCost = 0;
      let plannedOutsourceCost = 0;

      resourcesData.forEach(r => {
        if (r.start_month && r.end_month) {
          // YYYY-MM or YYYY-MM-DD format から年月を抽出（タイムゾーン問題を回避）
          const startYear = parseInt(r.start_month.substring(0, 4));
          const startMonth = parseInt(r.start_month.substring(5, 7));
          const endYear = parseInt(r.end_month.substring(0, 4));
          const endMonth = parseInt(r.end_month.substring(5, 7));

          // ターゲット年月が範囲内かチェック（YYYYMM形式で比較）
          const targetYearMonth = fiscalYear * 100 + m;
          const startYearMonth = startYear * 100 + startMonth;
          const endYearMonth = endYear * 100 + endMonth;

          if (targetYearMonth >= startYearMonth && targetYearMonth <= endYearMonth) {
            plannedSales += Number(r.unit_price) || 0;
            plannedCost += Number(r.cost_rate) || 0;
            // 外注タイプの場合は外注コストとしてカウント
            if (r.resource_type === 'contractor' || r.resource_type === 'outsource_pool') {
              plannedOutsourceCost += Number(r.cost_rate) || 0;
            }
          }
        }
      });

      const plannedOutsourceRate = plannedCost > 0 ? (plannedOutsourceCost / plannedCost) * 100 : 0;

      months.push({
        month: m,
        plannedSales,
        plannedCost,
        plannedOutsourceRate,
        actualSales: 0, // 実績は月次予実タブから取得（将来拡張）
        actualCost: 0,
        actualOutsourceRate: 0,
      });
    }

    return months;
  }, [resourcesData, fiscalYear]);

  // 指定年度の合計計算
  const yearlyTotals = useMemo(() => {
    const totals = monthlyBreakdown.reduce(
      (acc, m) => ({
        plannedSales: acc.plannedSales + m.plannedSales,
        plannedCost: acc.plannedCost + m.plannedCost,
        actualSales: acc.actualSales + m.actualSales,
        actualCost: acc.actualCost + m.actualCost,
      }),
      { plannedSales: 0, plannedCost: 0, actualSales: 0, actualCost: 0 }
    );
    return {
      ...totals,
      plannedGrossProfit: totals.plannedSales - totals.plannedCost,
      actualGrossProfit: totals.actualSales - totals.actualCost,
    };
  }, [monthlyBreakdown]);

  // 期待売上（totalContributionを使用 - フェーズ別貢献額の合計）
  // ※ individual_conversion_rateではなく、履歴方式の累積貢献額を使用
  const calculatedExpectedContribution = totalContribution;

  // 年度期待売上（totalContributionを年度売上比率で按分）
  const fiscalYearExpectedSales = useMemo(() => {
    const yearlyPlannedSales = yearlyTotals?.plannedSales || 0;
    const totalPlannedSales = calculatedFromResources.sales;
    if (totalPlannedSales === 0 || totalContribution === 0) return 0;
    return totalContribution * (yearlyPlannedSales / totalPlannedSales);
  }, [totalContribution, yearlyTotals, calculatedFromResources]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/sales/cases')} sx={{ mt: 2 }}>
          案件一覧に戻る
        </Button>
      </Box>
    );
  }

  if (!salesCase) {
    return (
      <Box>
        <Alert severity="warning">案件が見つかりませんでした</Alert>
        <Button onClick={() => navigate('/sales/cases')} sx={{ mt: 2 }}>
          案件一覧に戻る
        </Button>
      </Box>
    );
  }

  const statusInfo = getStatusLabel(salesCase.status, salesCase.close_result);
  const canEdit = user?.employee_id === salesCase.assigned_employee_id || salesPermissions?.can_manage_accounts; // 担当者または営業マスタ管理者は編集可能
  const canDelete = canEdit || salesPermissions?.can_manage_accounts; // 担当者または営業マスタ管理者は削除可能

  // 現在のフェーズ情報
  const currentPhase = phases.find(p => p.phase_id === salesCase.sales_phase_id);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          戻る
        </Button>
        <Box>
          {canEdit && (
            <Tooltip title={
              salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending' 
                ? '承認待ち' 
                : ''
            }>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => navigate(`/sales/cases/${caseId}/edit`)}
                  sx={{ mr: 1 }}
                  disabled={salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending'}
                >
                  編集
                </Button>
              </span>
            </Tooltip>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              削除
            </Button>
          )}
        </Box>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        案件詳細
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        案件No: {salesCase.case_no}
      </Typography>

      {salesCase.has_pending_preview && (
        <Alert
          severity={salesCase.approval_status === 'draft' || salesCase.approval_status === 'rejected' ? 'warning' : 'info'}
          sx={{ mb: 3 }}
        >
          {salesCase.approval_status === 'draft' || salesCase.approval_status === 'rejected'
            ? '表示されている内容は却下または取消された申請内容です。正本（業績計算）は承認済みの内容のままです。'
            : 'この案件は承認待ちです。表示されている内容は申請中の変更内容（承認後の状態）です。'}
        </Alert>
      )}

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            基本情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 案件名 */}
            <TextField
              label="案件名"
              value={salesCase.case_name}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />

            {/* 案件概要 */}
            <TextField
              label="案件概要"
              value={salesCase.case_summary || ''}
              multiline
              minRows={20}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />

            {/* 案件状態 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                案件状態
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="商談中"
                  color={salesCase.status === 'active' ? 'primary' : 'default'}
                  variant={salesCase.status === 'active' ? 'filled' : 'outlined'}
                />
                <Chip
                  label="保留"
                  color={salesCase.status === 'suspended' ? 'warning' : 'default'}
                  variant={salesCase.status === 'suspended' ? 'filled' : 'outlined'}
                />
                <Chip
                  label="受注"
                  color={salesCase.status === 'closed' && salesCase.close_result === 'won' ? 'success' : 'default'}
                  variant={salesCase.status === 'closed' && salesCase.close_result === 'won' ? 'filled' : 'outlined'}
                />
                <Chip
                  label="失注"
                  color={salesCase.status === 'closed' && salesCase.close_result === 'lost' ? 'error' : 'default'}
                  variant={salesCase.status === 'closed' && salesCase.close_result === 'lost' ? 'filled' : 'outlined'}
                />
              </Stack>
            </Box>

            {/* 事業カテゴリ */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                事業カテゴリ
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['SI経由開発', '日本国内開発', '商品・サービス販売', 'SES'].map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    color={salesCase.business_category === cat ? 'primary' : 'default'}
                    variant={salesCase.business_category === cat ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>

            {/* 営業フェーズ */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                営業フェーズ
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {phases.map((phase) => (
                  <Chip
                    key={phase.phase_id}
                    label={phase.phase_name}
                    color={salesCase.sales_phase_id === phase.phase_id ? 'primary' : 'default'}
                    variant={salesCase.sales_phase_id === phase.phase_id ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: salesCase.sales_phase_id === phase.phase_id ? phase.color_code : undefined,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* 外部プロジェクトID */}
            {salesCase.external_project_id && (
              <TextField
                label="外部プロジェクトID（原価システム）"
                value={salesCase.external_project_id}
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
              />
            )}

            {/* 次回アクション日 */}
            <TextField
              label="次回アクション日"
              type="date"
              value={salesCase.next_action_date ? salesCase.next_action_date.substring(0, 10) : ''}
              fullWidth
              disabled
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
            />

            {/* 次回アクション内容 */}
            <TextField
              label="次回アクション内容"
              value={salesCase.next_action_memo || ''}
              multiline
              rows={2}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* フェーズガイダンス */}
      {currentPhase && (currentPhase.phase_goal || currentPhase.required_actions?.length || currentPhase.suggested_next_actions?.length) && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light', bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              フェーズガイダンス: {salesCase.sales_phase_name}
            </Typography>

            {/* 滞留警告 */}
            {salesCase.days_in_phase !== undefined && salesCase.target_days !== undefined && salesCase.days_in_phase > salesCase.target_days && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                滞留警告: {salesCase.days_in_phase}日経過（目標: {salesCase.target_days}日以内）
              </Alert>
            )}

            {/* ゴール */}
            {currentPhase.phase_goal && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  このフェーズのゴール
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {currentPhase.phase_goal}
                </Typography>
              </Box>
            )}

            {/* やるべきこと */}
            {currentPhase.required_actions && currentPhase.required_actions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  やるべきこと
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {currentPhase.required_actions.map((item: any, index: number) => (
                    <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ color: 'text.secondary' }}>□</Box>
                      {typeof item === 'string' ? item : item.action}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* 推奨アクション候補 */}
            {currentPhase.suggested_next_actions && currentPhase.suggested_next_actions.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  推奨アクション候補
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {currentPhase.suggested_next_actions.map((action: string, index: number) => (
                    <Chip key={index} label={action} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* 要員計画 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            要員計画
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            案件に配置予定の要員情報です。
          </Typography>
          <ResourceAllocationTable
            resources={resourcesData}
            onChange={() => {}}
            disabled={true}
            fiscalYear={fiscalYear}
            showAlert={false}
          />
        </CardContent>
      </Card>

      {/* 金額情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">金額情報</Typography>
              {resourcesData.length > 0 && (
                <Chip label="要員配置から自動計算" size="small" color="info" />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {fiscalYears.map((year) => (
                <Chip
                  key={year}
                  label={`${year}年`}
                  size="small"
                  color={fiscalYear === year ? 'primary' : 'default'}
                  variant={fiscalYear === year ? 'filled' : 'outlined'}
                  onClick={() => setFiscalYear(year)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <TextField
                label="受注予定額"
                value={Math.floor(resourcesData.length > 0 ? calculatedFromResources.sales : salesCase.expected_order_amount || 0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="粗利額"
                value={Math.floor(resourcesData.length > 0 ? calculatedFromResources.grossProfit : salesCase.gross_profit || 0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="受注予定月"
                type="month"
                value={resourcesData.length > 0 && earliestStartMonth ? earliestStartMonth : (salesCase.expected_order_date ? salesCase.expected_order_date.substring(0, 7) : '')}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="期待売上"
                value={calculatedExpectedContribution.toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label={`${fiscalYear}年予定売上`}
                value={(yearlyTotals?.plannedSales || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年予定粗利`}
                value={(yearlyTotals?.plannedGrossProfit || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年期待売上`}
                value={fiscalYearExpectedSales.toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* 月別予実テーブル */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            月別予実（{fiscalYear}年）
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>月</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定外注率</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実外注率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyBreakdown.map((row) => (
                  <TableRow key={row.month} hover>
                    <TableCell>{row.month}月</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedSales)}</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedCost)}</TableCell>
                    <TableCell align="right">{row.plannedOutsourceRate.toFixed(0)}%</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualSales > 0 ? formatCurrencyWithUnit(row.actualSales) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? formatCurrencyWithUnit(row.actualCost) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? `${row.actualOutsourceRate.toFixed(0)}%` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* 合計行 */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedSales || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedCost || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {(yearlyTotals?.plannedCost || 0) > 0
                      ? `${((monthlyBreakdown.reduce((sum, m) => sum + (m.plannedOutsourceRate * m.plannedCost), 0) / (yearlyTotals?.plannedCost || 1))).toFixed(0)}%`
                      : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualSales || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualSales || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualCost || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualCost || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 行動履歴 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            行動履歴
          </Typography>
          <ActivityTimeline
            caseId={parseInt(caseId!)}
            activities={activities}
            readOnly={!canEdit}
            onAdd={canEdit ? handleAddActivity : undefined}
            onUpdate={canEdit ? handleUpdateActivity : undefined}
            onDelete={canEdit ? handleDeleteActivity : undefined}
          />
        </CardContent>
      </Card>

      {/* 営業担当者・取引先情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            営業担当者・取引先情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="営業担当者"
              value={salesCase.assigned_employee_name || salesCase.assigned_employee_id}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="サービス部門"
              value={salesCase.service_department || '-'}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="取引先"
              value={salesCase.account_name || '-'}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="エンドユーザー"
              value={salesCase.end_user || '-'}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* クローズ情報（クローズ済み案件のみ表示） */}
      {salesCase.status === 'closed' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              クローズ情報
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  クローズ結果
                </Typography>
                <Chip
                  label={salesCase.close_result === 'won' ? '受注' : '失注'}
                  color={salesCase.close_result === 'won' ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {salesCase.close_result === 'lost' && salesCase.close_reason_codes && salesCase.close_reason_codes.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    失注理由
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {salesCase.close_reason_codes.map((code) => {
                      const reason = closeReasons.find((r) => r.reason_code === code);
                      return (
                        <Chip
                          key={code}
                          label={reason ? `${reason.reason_name} (${reason.category})` : code}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                </Grid>
              )}
              {salesCase.close_reason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    クローズ理由詳細
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {salesCase.close_reason}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* フェーズ別期待受注額 */}
      {phaseContributions.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              フェーズ別期待受注額
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                期待受注額総額
              </Typography>
              <Typography variant="h5" color="primary">
                {formatCurrencyWithUnit(totalContribution)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ※各フェーズの期待受注額の合計
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>フェーズ</TableCell>
                    <TableCell>種別</TableCell>
                    <TableCell align="right">個別転換率</TableCell>
                    <TableCell align="right">予定受注額（当時）</TableCell>
                    <TableCell align="right">期待受注額</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phaseContributions.map((contribution) => (
                    <TableRow key={contribution.contribution_id}>
                      <TableCell>{contribution.phase_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={contribution.phase_type_label}
                          size="small"
                          color={contribution.is_actual_phase ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {contribution.individual_conversion_rate}%
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencyWithUnit(contribution.expected_order_amount_at_time)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={contribution.is_actual_phase ? 'bold' : 'normal'}
                        >
                          {formatCurrencyWithUnit(contribution.expected_contribution)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 変更履歴 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            変更履歴
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>変更者</TableCell>
                  <TableCell>承認者</TableCell>
                  <TableCell>フェーズ</TableCell>
                  <TableCell align="right">受注予定額</TableCell>
                  <TableCell>変更理由/コメント</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phaseHistory.length > 0 ? (
                  phaseHistory.map((history) => {
                    // change_typeに応じたラベルと色を設定
                    const getChangeTypeDisplay = (changeType: string | undefined) => {
                      switch (changeType) {
                        case 'initial':
                          return { label: '新規登録', color: 'info' as const };
                        case 'phase_change':
                          return { label: 'フェーズ変更', color: 'success' as const };
                        case 'amount_change':
                          return { label: '金額変更', color: 'warning' as const };
                        case 'approval_submit':
                          return { label: '承認申請', color: 'secondary' as const };
                        case 'approved':
                          return { label: '承認', color: 'success' as const };
                        case 'rejected':
                          return { label: '却下', color: 'error' as const };
                        case 'cancelled':
                          return { label: '申請取消', color: 'default' as const };
                        default:
                          return { label: '変更', color: 'default' as const };
                      }
                    };
                    const changeTypeDisplay = getChangeTypeDisplay(history.change_type);
                    
                    // 承認関連の場合はapproval_commentを表示、それ以外はchange_reasonを表示
                    const displayReason = ['approval_submit', 'approved', 'rejected'].includes(history.change_type || '')
                      ? (history.approval_comment || history.change_reason || '-')
                      : (history.change_reason || '-');

                    return (
                      <TableRow key={history.history_id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(history.changed_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={changeTypeDisplay.label}
                            color={changeTypeDisplay.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{history.changed_by_name || history.changed_by}</TableCell>
                        <TableCell>{history.approver_name || '-'}</TableCell>
                        <TableCell>{history.sales_phase_name || '-'}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(history.expected_order_amount)}</TableCell>
                        <TableCell>{displayReason}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">変更履歴がありません</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* メタ情報 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary', fontSize: '0.875rem' }}>
        <Typography variant="caption">
          登録者: {salesCase.created_by_name || '-'} ({formatDateTime(salesCase.created_at)})
        </Typography>
        <Typography variant="caption">
          更新者: {salesCase.updated_by_name || '-'} ({formatDateTime(salesCase.updated_at)})
        </Typography>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>案件削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            この案件を削除してもよろしいですか？
            <br />
            案件No: {salesCase.case_no}
            <br />
            案件名: {salesCase.case_name}
            <br />
            <br />
            この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            キャンセル
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesCaseDetailPage;
